import {
  type Job_SiteUsage_Read,
  type Job_SiteUsageEntry_Read,
  type Load_SiteUsage_Read,
  type Load_SiteUsageEntry_Read,
  type Order_SiteUsage_Read,
  type Order_SiteUsageEntry_Read,
  type SiteUsage_Job_Read,
  type SiteUsageLoad_Read,
  WaypointType,
} from '@treadinc/horizon-api-spec';

import { Geofence, Site } from '~hooks/useSites/models';

/**
 * A site is a default (candidate) site only when it has usages and *every* one
 * is a default — if any usage is a materialized waypoint, the materialized
 * version wins and the site renders as an actual waypoint.
 */
export const isDefaultSiteUsages = (usages: { defaultSite: boolean }[]): boolean =>
  usages.length > 0 && usages.every((u) => u.defaultSite);

class SiteUsageJob {
  public static parse(proto: SiteUsage_Job_Read) {
    return new SiteUsageJob(proto.id, proto.job_id);
  }

  public get id() {
    return this._id;
  }

  public get jobId() {
    return this._job_id;
  }

  constructor(
    private _id: string,
    private _job_id: string,
  ) {}
}

class SiteUsageLoad {
  public static parse(proto: SiteUsageLoad_Read) {
    return new SiteUsageLoad(proto.id, proto.load_id);
  }

  public get id() {
    return this._id;
  }

  public get loadId() {
    return this._load_id;
  }

  constructor(
    private _id: string,
    private _load_id: string,
  ) {}
}

export class JobSiteUsageEntry {
  public static parse(proto: Job_SiteUsageEntry_Read) {
    return new JobSiteUsageEntry(
      proto.waypoint_type,
      proto.loads.map(SiteUsageLoad.parse),
      proto.default_site,
    );
  }

  public get waypointType() {
    return this._waypoint_type;
  }

  public get loads() {
    return this._loads;
  }

  public get defaultSite() {
    return this._default_site;
  }

  constructor(
    private _waypoint_type: WaypointType,
    private _loads: SiteUsageLoad[],
    private _default_site: boolean,
  ) {}
}

class OrderSiteUsageEntry {
  public static parse(proto: Order_SiteUsageEntry_Read) {
    return new OrderSiteUsageEntry(
      proto.waypoint_type,
      proto.jobs.map(SiteUsageJob.parse),
      proto.default_site,
    );
  }

  public get waypointType() {
    return this._waypoint_type;
  }

  public get jobs() {
    return this._jobs;
  }

  public get defaultSite() {
    return this._default_site;
  }

  constructor(
    private _waypoint_type: WaypointType,
    private _jobs: SiteUsageJob[],
    private _default_site: boolean,
  ) {}
}

class LoadSiteUsageEntry {
  public static parse(proto: Load_SiteUsageEntry_Read) {
    return new LoadSiteUsageEntry(proto.waypoint_type, proto.default_site);
  }

  public get waypointType() {
    return this._waypoint_type;
  }

  public get defaultSite() {
    return this._default_site;
  }

  constructor(
    private _waypoint_type: WaypointType,
    private _default_site: boolean,
  ) {}
}

export class JobSiteUsage {
  public static parse(proto: Job_SiteUsage_Read) {
    return new JobSiteUsage(
      Site.parseNested(proto.site),
      (proto.site.additional_next_billion_geofences ?? []).map(Geofence.parse),
      proto.usages.map(JobSiteUsageEntry.parse),
    );
  }

  public get site() {
    return this._site;
  }

  public get additionalGeofences() {
    return this._additional_geofences;
  }

  public get usages() {
    return this._usages;
  }

  constructor(
    private _site: Site,
    private _additional_geofences: Geofence[],
    private _usages: JobSiteUsageEntry[],
  ) {}
}

export class OrderSiteUsage {
  public static parse(proto: Order_SiteUsage_Read) {
    return new OrderSiteUsage(
      Site.parseNested(proto.site),
      (proto.site.additional_next_billion_geofences ?? []).map(Geofence.parse),
      proto.usages.map(OrderSiteUsageEntry.parse),
    );
  }

  public get site() {
    return this._site;
  }

  public get additionalGeofences() {
    return this._additional_geofences;
  }

  public get usages() {
    return this._usages;
  }

  constructor(
    private _site: Site,
    private _additional_geofences: Geofence[],
    private _usages: OrderSiteUsageEntry[],
  ) {}
}

export class LoadSiteUsage {
  public static parse(proto: Load_SiteUsage_Read) {
    return new LoadSiteUsage(
      Site.parseNested(proto.site),
      (proto.site.additional_next_billion_geofences ?? []).map(Geofence.parse),
      proto.usages.map(LoadSiteUsageEntry.parse),
    );
  }

  public get site() {
    return this._site;
  }

  public get additionalGeofences() {
    return this._additional_geofences;
  }

  public get usages() {
    return this._usages;
  }

  constructor(
    private _site: Site,
    private _additional_geofences: Geofence[],
    private _usages: LoadSiteUsageEntry[],
  ) {}
}
